import { useDebounceValue } from '@dni-hooks/useDebounceValue'
import { useApiCampaignCalculate } from '@hooks-api/fortune/useApiCampaignCalculate'
import { type CampaignCalculateRequest } from '@hooks-api/types/apiRequest'
import { useMemo } from 'react'
import { useFpCampaignCartStore } from './useFpCampaignCartStore'

export const useFpCampaignCartCalculate = (campaignId: number | null, campaignCode: string | undefined) => {
  const [cartValues] = useFpCampaignCartStore(campaignId)

  // 需要用 useMemo 來避免每次 render 時 value 的 reference 變更，否則會導致無窮迴圈
  const value = useMemo(() => {
    const request: CampaignCalculateRequest = {
      billing_country: cartValues.billing.billingCountry,
      campaign_code: campaignCode,
      order_items: cartValues.cart.items.map(cartItem => ({
        sku: cartItem.sku,
        quantity: cartItem.quantity,
        is_addon: !!cartItem.is_addon,
        is_addon_v2: !!cartItem.is_addon_v2,
        project_code: cartItem.project_code,
        cartItemId: cartItem.cartItemId,
        id: cartItem.id,
        is_out_of_stock: cartItem.is_out_of_stock,
        name: cartItem.name,
        original_price: cartItem.original_price,
        product_id: cartItem.product_id,
        sale_price: cartItem.sale_price,
        stock: cartItem.stock,
        variation_id: cartItem.variation_id,
        weight_attribute: cartItem.weight_attribute,
        addon_setting_id: cartItem.addon_setting_id ?? null,
        addon_main_product_id: cartItem.addon_main_product_id ?? null,
      })),
      applied_shipping_method_id: cartValues.shipping.appliedShippingMethodId,
      manual_input_coupon_ids: cartValues.coupon.manualInputCouponIds,
    }
    return request
  }, [campaignCode, cartValues.billing.billingCountry, cartValues.cart.items, cartValues.coupon.manualInputCouponIds, cartValues.shipping.appliedShippingMethodId])

  const debouncedValue = useDebounceValue(value, 200)
  const response = useApiCampaignCalculate(debouncedValue, {
    keepPreviousData: true,
  })

  // value !== debouncedValue 表示 cartValues 剛改、200ms debounce 視窗還沒過完
  // isValidating 表示 SWR 正在打 /calculate（含 keepPreviousData 的 stale-while-revalidate）
  // 兩者皆 false 時 destructuredData 才反映目前 cartValues，避免 user 在後端 cart_request_cache 同步前送出 checkout
  const isStale = value !== debouncedValue || response.isValidating

  return {
    ...response,
    calculateValue: value,
    isStale,
  }
}
